import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { supabase } from './supabase';
import CONFIG from './config';
import { useI18n } from './i18n';
import { useAuth } from './AuthContext';
import { fetchWithTimeout, readJsonResponse, responseError } from './apiRequest';
import './McpAccess.css';

const COPY = {
  en: {
    heading: 'Connect your AI assistant', manage: 'Connected AI assistants', loading: 'Loading connection…',
    intro: 'Review what this assistant can do with your InstantTranscriber account.',
    permissions: ['Start audio transcriptions using your API allowance.', 'Read your API and MCP transcriptions.', 'Retrieve available public YouTube captions without using audio hours.'],
    limits: 'API and MCP share one allowance: Free includes 1 audio hour per calendar month; Premium includes 15 hours per billing month. The API Plan includes 100 hours. Any enabled API Plan overage also applies to MCP.',
    approve: 'Allow connection', deny: 'Cancel', busy: 'Connecting…', back: 'Back to your profile',
    destination: 'Connection destination',
    error: 'The connection could not be loaded. Start again from your AI assistant.',
    empty: 'You have no connected AI assistants.', revoke: 'Disconnect', docs: 'Read the setup guide',
    note: 'You can disconnect an assistant here at any time. API keys are managed separately in your profile.',
  },
  de: {
    heading: 'KI-Assistenten verbinden', manage: 'Verbundene KI-Assistenten', loading: 'Verbindung wird geladen…',
    intro: 'Prüfe, was dieser Assistent mit deinem InstantTranscriber-Konto tun darf.',
    permissions: ['Audiotranskriptionen aus deinem API-Kontingent starten.', 'Deine API- und MCP-Transkriptionen lesen.', 'Vorhandene öffentliche YouTube-Untertitel ohne Verbrauch von Audiostunden abrufen.'],
    limits: 'API und MCP teilen sich ein Kontingent: Free enthält 1 Audiostunde pro Kalendermonat, Premium 15 Stunden pro Abrechnungsmonat. Der API-Plan enthält 100 Stunden. Ein aktivierter Mehrverbrauch im API-Plan gilt auch für MCP.',
    approve: 'Verbindung erlauben', deny: 'Abbrechen', busy: 'Wird verbunden…', back: 'Zurück zum Profil',
    destination: 'Verbindungsziel',
    error: 'Die Verbindung konnte nicht geladen werden. Starte erneut in deinem KI-Assistenten.',
    empty: 'Du hast noch keine KI-Assistenten verbunden.', revoke: 'Trennen', docs: 'Einrichtungsanleitung lesen',
    note: 'Du kannst einen Assistenten hier jederzeit trennen. API-Schlüssel verwaltest du separat im Profil.',
  },
};

const DESKTOP_COPY = {
  en: {
    ...COPY.en, heading: 'Connect InstantTranscriber Desktop', manage: 'Connected desktop apps',
    intro: 'Connect this computer to dictate into your apps. The app records only while you hold your shortcut or explicitly start a recording.',
    permissions: ['Transcribe short microphone recordings using your shared audio allowance.', 'Read its own dictation results for delivery to your computer.'],
    limits: 'Desktop, API and MCP share one allowance: Free includes 1 audio hour per calendar month; Premium includes 15 hours per billing month. The API Plan includes 100 hours. Desktop dictation never uses paid overage. Only successful dictations consume time, rounded up to the next second.',
    approve: 'Connect this computer', empty: 'You have no connected desktop apps.',
    note: 'Disconnect a computer here at any time. The app stores its sign-in in your operating system’s credential store. Audio is removed after processing; dictation results are available for recovery for 15 minutes.',
    error: 'This connection request could not be loaded. Start sign-in again in the desktop app.',
  },
  de: {
    ...COPY.de, heading: 'InstantTranscriber Desktop verbinden', manage: 'Verbundene Desktop-Apps',
    intro: 'Verbinde diesen Computer, um in deine Apps zu diktieren. Die App nimmt nur auf, während du den Hotkey hältst oder eine Aufnahme ausdrücklich startest.',
    permissions: ['Kurze Mikrofonaufnahmen mit deinem gemeinsamen Audiokontingent transkribieren.', 'Eigene Diktierergebnisse zur Übertragung an diesen Computer abrufen.'],
    limits: 'Desktop, API und MCP teilen sich ein Kontingent: Free enthält 1 Audiostunde pro Kalendermonat, Premium 15 Stunden pro Abrechnungsmonat. Der API-Plan enthält 100 Stunden. Desktop-Diktate nutzen keinen kostenpflichtigen Mehrverbrauch. Nur erfolgreiche Diktate verbrauchen Zeit, auf die nächste Sekunde aufgerundet.',
    approve: 'Diesen Computer verbinden', empty: 'Du hast noch keine Desktop-Apps verbunden.',
    note: 'Du kannst einen Computer hier jederzeit trennen. Die App speichert die Anmeldung im Zugangsdaten-Speicher deines Betriebssystems. Audio wird nach der Verarbeitung gelöscht; Diktierergebnisse können 15 Minuten lang erneut abgerufen werden.',
    error: 'Die Verbindungsanfrage konnte nicht geladen werden. Starte die Anmeldung in der Desktop-App erneut.',
  },
};

async function accountRequest(path, options = {}, kind = 'mcp') {
  const { data } = await supabase.auth.getSession();
  const token = data?.session?.access_token;
  if (!token) throw new Error('Please sign in again.');
  const response = await fetchWithTimeout(`${CONFIG.API_BASE}/${kind}/oauth/${path}`, {
    ...options, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  }, 20000);
  if (response.status === 204) return {};
  const payload = await readJsonResponse(response);
  if (!response.ok) throw responseError(response, payload, 'MCP connection failed.');
  return payload;
}

export default function McpAccess({ navigateTo = (url) => window.location.assign(url), kind = 'mcp' }) {
  const location = useLocation();
  const { language } = useI18n();
  const { currentUser } = useAuth();
  const accountId = currentUser?.id;
  const copies = kind === 'desktop' ? DESKTOP_COPY : COPY;
  const copy = copies[language] || copies.en;
  const consent = location.pathname.endsWith('/authorize');
  const ticket = new URLSearchParams(location.search).get('request') || '';
  const [pending, setPending] = useState(null);
  const [connections, setConnections] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const locked = useRef(false);
  const ready = pending?.requestTicket === ticket && pending?.accountId === accountId;

  useEffect(() => {
    let live = true;
    setError(''); setPending(null); setConnections(null);
    const request = consent
      ? ticket ? accountRequest(`pending/${encodeURIComponent(ticket)}`, {}, kind) : Promise.reject(new Error(copy.error))
      : accountRequest('connections', {}, kind);
    request.then((value) => {
      if (live) { if (consent) setPending({ ...value, requestTicket: ticket, accountId }); else setConnections(value.connections || []); }
    }).catch(() => { if (live) setError(copy.error); });
    return () => { live = false; };
  }, [consent, ticket, copy.error, accountId, kind]);

  const decide = async (allow) => {
    if (locked.current || !ready) return;
    locked.current = true;
    setBusy(true); setError('');
    try {
      const value = await accountRequest(`pending/${encodeURIComponent(ticket)}`, {
        method: 'POST', body: JSON.stringify({ allow }),
      }, kind);
      navigateTo(value.redirect_url);
    } catch {
      setError(copy.error); setBusy(false); locked.current = false;
    }
  };
  const disconnect = async (id) => {
    if (locked.current) return;
    locked.current = true; setBusy(true); setError('');
    try {
      await accountRequest(`connections/${encodeURIComponent(id)}`, { method: 'DELETE' }, kind);
      setConnections((items) => items.filter((item) => item.id !== id));
    } catch { setError(copy.error); }
    finally { locked.current = false; setBusy(false); }
  };

  return <main className="mcp-access">
    <Link to="/dashboard/profile">← {copy.back}</Link>
    <section className="mcp-access-card">
      <p className="mcp-access-eyebrow">InstantTranscriber · {kind === 'desktop' ? 'Desktop · Preview' : 'MCP'}</p>
      <h1>{consent ? copy.heading : copy.manage}</h1>
      {error && <p className="mcp-access-error" role="alert">{error}</p>}
      {consent && ready && <>
        <p>{copy.intro}</p>
        <h2>{pending.client_name}</h2>
        {pending.redirect_origin && <p>{copy.destination}: <code>{pending.redirect_origin}</code></p>}
        <ul>{copy.permissions.map((text) => <li key={text}>{text}</li>)}</ul>
        <p className="mcp-access-limits">{copy.limits}</p>
        <div className="mcp-access-actions">
          <button className="mcp-access-primary" type="button" disabled={busy} onClick={() => decide(true)}>{busy ? copy.busy : copy.approve}</button>
          <button type="button" disabled={busy} onClick={() => decide(false)}>{copy.deny}</button>
        </div>
      </>}
      {!consent && connections && <>
        <p>{copy.note}</p>
        {connections.length === 0 ? <p>{copy.empty}</p> : <ul className="mcp-access-connections">{connections.map((item) =>
          <li key={item.id}><strong>{item.client_name}</strong><button type="button" disabled={busy} onClick={() => disconnect(item.id)}>{copy.revoke}</button></li>)}</ul>}
      </>}
      {!error && (consent ? !ready : !connections) && <p role="status">{copy.loading}</p>}
      {kind === 'mcp' && <a href="https://instanttranscriber.com/mcp">{copy.docs}</a>}
    </section>
  </main>;
}
